<script setup>
import moment from 'moment'
import { useApi } from '@/composables/useApi'
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import HorizontalStackBar from '@/@core/components/graph/HorizontalStackBar.vue'
import HorizontalBar from '@/@core/components/graph/HorizontalBar.vue'
import VerticalBar from '@/@core/components/graph/VerticalBar.vue'
import { VSkeletonLoader } from 'vuetify/lib/labs/components.mjs'
import { cookieRef } from '@/@layouts/stores/config'
import SubscriptionTable from '@/views/survey/SubscriptionTable.vue'

definePage({
  meta: {
    name: "Survey Analytics",
  },
})

const route = useRoute()
const router = useRouter()

const unitId = computed(() => cookieRef('selectedUnit').value ?? null)

const surveyType = computed(() => route.params.type)
const surveyId = computed(() => route.params.survey ?? null)

const survey = ref()

// Validate Survey Type
if (!surveyType) {
  router.push({
    name: 'invalid-url',
    params: { code: 404 },
  })
}

if (surveyId.value) {
  await useApi(createUrl(`/survey/surveys/${surveyId.value}`), {
    beforeFetch: () => {},
    afterFetch: ({ data }) => {
      survey.value = data
    },
    onFetchError: error => {
      router.push({
        name: 'invalid-url',
        params: { code: 404 },
      })
    },
  })
}

// Default date range - Last 30 days
const defaultFromDate = moment().subtract(30, 'days').format('YYYY-MM-DD')
const defaultToDate = moment().format('YYYY-MM-DD')

// Filter State
const selectedSchedule = ref(null)
const scheduleDateRange = ref(`${defaultFromDate} to ${defaultToDate}`)
const fromDate = ref(defaultFromDate)
const toDate = ref(defaultToDate)

// Schedules computed from survey
const schedules = computed(() => survey.value?.schedules ?? [])

// Date Range Options
const dateRangeOptions = ref({
  mode: 'range',
  maxDate: new Date(),
  dateFormat: 'Y-m-d',
  enableTime: false,
  defaultDate: [defaultFromDate, defaultToDate],
  onChange: (selectedDates, dateStr) => {
    if (selectedDates.length > 0) {
      fromDate.value = moment(selectedDates[0]).format('YYYY-MM-DD')
      toDate.value = selectedDates[1]
        ? moment(selectedDates[1]).format('YYYY-MM-DD')
        : fromDate.value
    }
  },
})

// Computed filter query params
const filterQuery = computed(() => ({
  unit_id: unitId.value,
  survey_schedule_id: selectedSchedule.value,
  schedule_at: [fromDate.value, toDate.value],
  
  // from_date: fromDate.value,
  // to_date: toDate.value,
}))

useApi(createUrl(`/survey/surveys/${surveyId.value}/analytics`, {
  query: filterQuery,
}), {
  beforeFetch: () => {},
  afterFetch: ({ data }) => {
    survey.value = {
      ...survey.value,
      ...data,
    }
  },
})

// Load Form
const {
  isFetching: isLoadingFormSteps,
  data: formSteps,
} = useApi(createUrl(`/survey/form-steps`, {
  query: {
    search: { 
      survey_id: surveyId.value,
    },
    with: ['questionGroups'],  
  }, 
}))

const questions = ref([])
const isCalculatingQuestionAnalytics = ref(false)

const calculateQuestionAnalytics = () => {
  isCalculatingQuestionAnalytics.value = true

  formSteps.value.forEach(step => {
    step.question_groups.forEach(group => {
      const filterQuestions = group.questions.filter(item => ['select', 'radio', 'radio_rating', 'star_rating', 'smile', 'scale'].includes(item.question_type))

      filterQuestions.forEach(question => {
        // Load Question Response
        useApi(createUrl(`/survey/questions/${question.id}`, {
          query: filterQuery,
        }), {
          afterFetch: ({ data }) => {
            const grouped = data.answers.reduce((acc, item) => {
              const option = question.question_options.find(x => x.id === item.question_option_id)
    
              if (!acc[option.id]) {
                acc[option.id] = {
                  question_option_id: option.id,
                  label: option.label,
                  value: option.value,
                  count: 0,
                  responses: [],
                  color: option.color,
                }
              }
    
              acc[option.id].count += 1
              acc[option.id].responses.push(item)
    
              return acc
            }, {})

            const totalAnswers = data.answers.length

            let graphData = Object.values(grouped).map(group => ({
              question_option_id: group.question_option_id,
              label: group.label,
              value: group.value,
              count: group.count,
              percentage: ((group.count / totalAnswers) * 100).toFixed(2),
              color: group.color,
            }))

            // For Scale Type Questions, include all options even if count is 0
            if (question.question_type === 'scale') {
              const scaleColors = colorPaletteForNPS()

              graphData = question.question_options.map((item, optionIndex) => {
                let data = graphData.find(x => x.question_option_id === item.id)

                return {
                  question_option_id: item.id,
                  label: item.label,
                  value: item.value,
                  count: data?.count ?? 0,
                  percentage: data?.percentage ?? 0,
                  color: item.color ?? scaleColors[optionIndex],
                }
              })
            }

            if (totalAnswers > 0) {
              questions.value.push({
                ...data,
                total_answers: totalAnswers,
                graph_data: graphData,
              })
            }
          },
        })
      })
    })
  })

  isCalculatingQuestionAnalytics.value = false
}

watch(() => formSteps.value, value => {
  if (value) {
    calculateQuestionAnalytics()
  }
}, {
  deep: true,
})

watch(() => filterQuery.value, () => {
  questions.value = []
  calculateQuestionAnalytics()
}, {
  deep: true,
})

const isLoading = computed(() => isLoadingFormSteps.value || isCalculatingQuestionAnalytics.value)

// Subscription Table Dialog
const isSubscriptionDialogVisible = ref(false)
const subscriptionTableFilter = ref(null)

const handleDataPointClick = data => {
  subscriptionTableFilter.value = {
    ...filterQuery.value,
    question_option_id: data.question_option_id,
  }
  isSubscriptionDialogVisible.value = true
}

watch(
  () => isSubscriptionDialogVisible.value,
  value => {
    if (value === false) subscriptionTableFilter.value = null
  },
)
</script>

<template>
  <VRow v-if="isLoading">
    <VSkeletonLoader
      class="mx-auto"
      type="card"
      width="100%"
    />
  </VRow>
  <VRow>
    <VCol cols="12">
      <div class="d-flex align-center flex-wrap gap-4">
        <div class="d-flex align-center gap-2">
          <h4 class="text-h3 font-weight-bold text-primary text-capitalize">
            {{ survey.title }}
          </h4>
          <!--
            <VBtn
            icon
            size="small"
            density="compact"
            variant="text"
            color="primary"
            :to="{ name: 'app-survey-customer-setup', params: { type: surveyType, survey: surveyId } }"
            >
            <VIcon
            icon="tabler-settings"
            size="20"
            />
            <VTooltip
            activator="parent"
            location="top"
            >
            Settings
            </VTooltip>
            </VBtn>
            <VBtn
            icon
            size="small"
            variant="text"
            color="primary"
            :to="{ name: 'app-survey-collection-customer', params: { id: surveyId } }"
            >
            <VIcon
            icon="tabler-user-screen"
            size="20"
            />
            <VTooltip
            activator="parent"
            location="top"
            >
            View Responses
            </VTooltip>
            </VBtn> 
          -->
        </div>
        <VSpacer />
        <div class="d-flex align-center flex-wrap gap-4">
          <AppSelect
            v-model="selectedSchedule"
            placeholder="Select Schedule"
            :items="schedules"
            :item-title="item => item.name"
            :item-value="item => item.id"
            clearable
            style="min-inline-size: 200px;"
          />
          <AppDateTimePicker
            v-model="scheduleDateRange"
            placeholder="Select Date Range"
            :config="dateRangeOptions"
            style="min-inline-size: 250px;"
          />
        </div>
      </div>
    </VCol>

    <VRow v-if="questions?.length > 0">
      <VCol cols="12">
        <VRow>
          <!-- Total Scheduled Surveys Card -->
          <VCol
            cols="12"
            sm="4"
          >
            <VCard
              elevation="2"
              class="pa-4"
            >
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-h4 font-weight-bold primary--text">
                    {{ survey.number_of_scheduled_surveys }}
                  </div>
                  <div class="text-subtitle-1 text-medium-emphasis mt-1">
                    Scheduled Surveys
                  </div>
                </div>
                <VAvatar
                  color="primary"
                  size="56"
                  class="elevation-1"
                >
                  <VIcon
                    icon="tabler-clipboard-text"
                    size="32"
                  />
                </VAvatar>
              </div>
            </VCard>
          </VCol>

          <!-- Collected Surveys Card -->
          <VCol
            cols="12"
            sm="4"
          >
            <VCard
              elevation="2"
              class="pa-4"
            >
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-h4 font-weight-bold success--text">
                    {{ survey.number_of_collected_surveys }}
                  </div>
                  <div class="text-subtitle-1 text-medium-emphasis mt-1">
                    Collected Surveys
                  </div>
                </div>
                <VAvatar
                  color="success"
                  size="56"
                  class="elevation-1"
                >
                  <VIcon
                    icon="tabler-checkup-list"
                    size="32"
                  />
                </VAvatar>
              </div>
            </VCard>
          </VCol>

          <!-- Not Collected Surveys Card -->
          <VCol
            cols="12"
            sm="4"
          >
            <VCard
              elevation="2"
              class="pa-4"
            >
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-h4 font-weight-bold warning--text">
                    {{ survey.number_of_not_collected_surveys }}
                  </div>
                  <div class="text-subtitle-1 text-medium-emphasis mt-1">
                    Not Collected Surveys
                  </div>
                </div>
                <VAvatar
                  color="warning"
                  size="56"
                  class="elevation-1"
                >
                  <VIcon
                    icon="tabler-clipboard-off"
                    size="32"
                  />
                </VAvatar>
              </div>
            </VCard>
          </VCol>
        </VRow>
      </VCol>

      <template
        v-for="(question, questionIndex) in questions"
        :key="questionIndex"
      >
        <VCol cols="12">
          <VCard>
            <VCardItem>
              <VCardTitle
                class="text-h6"
                style="white-space: normal;"
              >
                {{ question.label }}
              </VCardTitle>
            </VCardItem>
            <VDivider />
            <VCardText>
              <HorizontalStackBar
                v-if="question.question_options.length <= 3"
                :graph-data="question.graph_data"
                @on-click-data-point="handleDataPointClick"
              />
              <VerticalBar
                v-else-if="question.question_options.length >= 9 || question.question_type === 'scale' || question.question_type === 'radio_rating' || question.question_type === 'star_rating' || question.question_type === 'smile'"
                :graph-data="question.graph_data"
                @on-click-data-point="handleDataPointClick"
              />
              <HorizontalBar
                v-else-if="question.question_options.length >= 4 && question.question_options.length <= 8"
                :graph-data="question.graph_data"
                @on-click-data-point="handleDataPointClick"
              />
            </VCardText>
          </VCard>
        </VCol>
      </template>
    </VRow>

    <VCol v-else>
      <VCard>
        <VCardText>
          <span class="d-block text-center">
            No analytics data available for this survey
          </span>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>

  <!--
    <VCard class="mt-8">
    <VCardItem title="IF The Graph Data Count IS Above OR Equal to 5 ">
    <template #append>
    <MoreBtn />
    </template>
    </VCardItem>

    <VCardText>
    <HorizontalBar :graph-data="testingGraph" />
    </VCardText>
    </VCard>

    <VCard class="mt-8">
    <VCardItem title="IF The Question Type NPS">
    <template #append>
    <VBtn
    variant="tonal"
    append-icon="tabler-chevron-down"
    >
    January
    </VBtn>
    </template>
    </VCardItem>

    <VCardText>
    <VerticalBar :graph-data="testingGraph" />
    </VCardText>
    </VCard>
  -->

  <!-- Subscription Table Dialog -->
  <VDialog v-model="isSubscriptionDialogVisible">
    <DialogCloseBtn
      size="small"
      @click="() => (isSubscriptionDialogVisible = !isSubscriptionDialogVisible)"
    />

    <VCard>
      <VCardText class="pa-0">
        <SubscriptionTable
          :headers="[
            { title: 'Redirect', key: 'redirect', sortable: false },
            { title: 'UHID', key: 'customer.uhid' },
            { title: 'Name', key: 'customer.name' },
            { title: 'Phone', key: 'customer.phone', },
            { title: 'Discharge At', key: 'episode.discharge_date' },
            { title: 'Collected At', key: 'collected_at' },
          ]"
          :filters="subscriptionTableFilter"
        />
      </VCardText>
    </VCard>
  </VDialog>
</template>
